/**
 * DeviceDetailModal — 디바이스 3D 디스펜서 + 양방향 시뮬 컨트롤 (M9 핵심).
 *
 * sim.state 로 환자 기준정보 + 28 포켓 현재 상태를 로드해 3D 에 반영하고,
 * 5초 폴링으로 DB 와 실시간 동기화한다. 포켓 클릭(복용/되돌림)은 즉시 DB 에 적재된다.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Modal, Row, Col, Card, Tag, Spin, Typography, Button, message } from 'antd'
import { Canvas } from '@react-three/fiber'
import { medApi, toErrMsg } from './api'
import { useLabel } from '@/framework/useLabel'
import DispenserScene, { type PocketInfo } from './DispenserScene'
import SimControlPanel from './SimControlPanel'
import { speak, isVoiceMuted, setVoiceMuted, VOICE_SLOT_KEY } from './simVoice'

interface Props {
  open: boolean              // 모달 표시 여부
  deviceCode: string | null  // 조회 대상 디바이스 코드(null=닫힘)
  onClose: () => void        // 닫기 콜백
}

interface DeviceMeta {
  patient_name: string | null   // 배정 환자명(미배정 null)
  patient_code: string | null   // 배정 환자 코드
  location: string | null       // 설치 위치
  status: string | null         // 디바이스 상태
  timezone?: string | null      // 환자 타임존(점멸 요일/슬롯 계산 기준)
  slot_times: Record<string, string>  // 슬롯별 시각(morning→08:00 등)
}

const SLOT_KEY = ['morning', 'lunch', 'dinner', 'bedtime'] as const
const DAY_KEY = ['SIM_DAY_MON', 'SIM_DAY_TUE', 'SIM_DAY_WED', 'SIM_DAY_THU', 'SIM_DAY_FRI', 'SIM_DAY_SAT', 'SIM_DAY_SUN']
const DAY_FALLBACK = ['월', '화', '수', '목', '금', '토', '일']

// 환자 타임존 기준 현재 요일(JS 0=일)·시각 — tz 없으면 뷰어 브라우저 로컬 폴백.
// 약달력 점멸은 '환자 현지 시각' 기준이어야 정합(뷰어가 다른 TZ/UTC 여도 전날로 안 밀림).
const _WD: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }
function tzNow(tz?: string | null): { jsDay: number; hour: number } {
  const now = new Date()
  if (!tz) return { jsDay: now.getDay(), hour: now.getHours() }
  try {
    const wd = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short' }).format(now)
    const hour = Number(
      new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: '2-digit', hour12: false, hourCycle: 'h23' }).format(now),
    )
    return { jsDay: _WD[wd] ?? now.getDay(), hour: Number.isFinite(hour) ? hour : now.getHours() }
  } catch {
    return { jsDay: now.getDay(), hour: now.getHours() }
  }
}

// 환자 TZ 기준 자정부터의 분(分) — '5분 전' 안내 계산용
function tzMinutes(tz?: string | null): number {
  const now = new Date()
  try {
    const s = new Intl.DateTimeFormat('en-US', {
      timeZone: tz || undefined, hour: '2-digit', minute: '2-digit', hour12: false, hourCycle: 'h23',
    }).format(now)
    const [h, m] = s.split(':').map(Number)
    if (Number.isFinite(h) && Number.isFinite(m)) return h * 60 + m
  } catch { /* fall through */ }
  return now.getHours() * 60 + now.getMinutes()
}

// 현재 시각 → 복용 시간대 슬롯 (0:아침 1:점심 2:저녁 3:취침)
function currentSlot(tz?: string | null): number {
  const h = tzNow(tz).hour
  if (h >= 5 && h < 11) return 0
  if (h >= 11 && h < 15) return 1
  if (h >= 15 && h < 21) return 2
  return 3
}

// JS getDay()(0=일) → day_of_week(0=월..6=일) — pocket_index 의 day 축과 일치
function todayColumn(tz?: string | null): number {
  return (tzNow(tz).jsDay + 6) % 7
}

/** 3D 디스펜서 시뮬 모달 — 실데이터 연동 + 셀 LED 점멸 + 5초 실시간 폴링. */
export default function DeviceDetailModal({ open, deviceCode, onClose }: Props) {
  const { t, locale } = useLabel()
  const [voiceOn, setVoiceOn] = useState(!isVoiceMuted())
  const SLOT_NAME = [
    t('LBL_SLOT_MORNING', '아침'),
    t('LBL_SLOT_LUNCH', '점심'),
    t('LBL_SLOT_DINNER', '저녁'),
    t('LBL_SLOT_BEDTIME', '취침'),
  ]
  const DAY_LABEL = DAY_KEY.map((k, i) => t(k, DAY_FALLBACK[i]))
  const [pockets, setPockets] = useState<Record<number, PocketInfo>>({})
  const [device, setDevice] = useState<DeviceMeta | null>(null)
  const [logs, setLogs] = useState<string[]>([])
  const [activeSlot, setActiveSlot] = useState<number>(() => currentSlot())
  const [stageReady, setStageReady] = useState(false)
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null)
  const [weekOffset, setWeekOffset] = useState(0)  // 0=이번주 / -1=전주 / +1=다음주
  const [week, setWeek] = useState<{ start: string; end: string; is_current: boolean } | null>(null)
  const busyRef = useRef(false)
  const todayCol = todayColumn(device?.timezone)  // 환자 TZ 기준 오늘 열(점멸 정합)

  // 음성 안내 — 요일+시점 포함. mp3(neural) 우선, 폴백 브라우저 TTS(simVoice).
  const announce = (event: string, dow?: number, slot?: number) => {
    const day = dow != null ? `${DAY_LABEL[dow]}${t('SIM_DAY_SUFFIX', '요일')}` : ''
    const slotW = slot != null ? SLOT_NAME[slot] : ''
    let text = ''
    let key = ''
    if (event === 'take') { text = `${day} ${slotW} ${t('SIM_VOICE_TAKE', '약 복용입니다')}`; key = `take_${dow}_${VOICE_SLOT_KEY[slot as number]}` }
    else if (event === 'fill') { text = `${day} ${slotW} ${t('SIM_VOICE_FILL', '약 채움입니다')}`; key = `fill_${dow}_${VOICE_SLOT_KEY[slot as number]}` }
    else if (event === 'due') { text = `${day} ${slotW} ${t('SIM_VOICE_DUE', '약 복용 시간입니다')}`; key = `due_${dow}_${VOICE_SLOT_KEY[slot as number]}` }
    else if (event === 'notyet') { text = t('SIM_VOICE_NOTYET', '아직은 약 복용 시간이 안 되었습니다. 외출하지 않으시면 자리에 놓아 주세요.'); key = 'notyet' }
    else if (event === 'done') { text = t('SIM_VOICE_DONE', '오늘 약을 모두 잘 드셨어요'); key = 'done' }
    else if (event === 'refilled') { text = t('SIM_VOICE_REFILLED', '약 채움이 완료되었습니다'); key = 'refilled' }
    speak(key, text.trim().replace(/\s+/g, ' '), locale)
  }
  // 타이머(5분 전/완료)에서 최신 상태·함수 접근용 ref
  const announceRef = useRef(announce); announceRef.current = announce
  const pocketsRef = useRef(pockets); pocketsRef.current = pockets
  const deviceRef = useRef(device); deviceRef.current = device
  const dueSaid = useRef<Set<string>>(new Set())
  const doneSaid = useRef<string>('')

  const pushLog = (line: string) => {
    setLogs((prev) => [`${new Date().toLocaleTimeString('ko-KR')}  ${line}`, ...prev].slice(0, 30))
  }

  const loadState = useCallback(async () => {
    if (!deviceCode) return
    try {
      const r = (await medApi.sim.state(deviceCode, weekOffset)) as {
        device?: DeviceMeta
        week?: { start: string; end: string; is_current: boolean }
        pockets?: {
          pocket_index: number
          is_filled: number
          medication_id: number | null
          medication_name: string | null
          dose: number | null
          status?: string
        }[]
      }
      if (r.device) setDevice(r.device)
      if (r.week) setWeek(r.week)
      const map: Record<number, PocketInfo> = {}
      for (const p of r.pockets ?? []) {
        map[p.pocket_index] = {
          filled: p.is_filled === 1,
          active: p.medication_id != null,
          medName: p.medication_name ?? null,
          dose: p.dose != null ? Number(p.dose) : null,
          status: p.status ?? null,   // 대상 주 복용상태(약달력 색) — pending|taken|late|missed
        }
      }
      setPockets(map)
    } catch {
      // 백엔드 미연결 — 데모 fallback (점심 슬롯 제외 21 포켓 활성)
      const map: Record<number, PocketInfo> = {}
      for (let i = 0; i < 28; i++) {
        const active = i % 4 !== 1
        map[i] = { filled: active, active, medName: active ? t('SIM_DEMO_MED', '데모 약') : null, dose: active ? 1 : null }
      }
      setPockets(map)
    }
  }, [deviceCode, weekOffset])

  // 모달 오픈 동안 5초 폴링으로 DB 와 실시간 동기화
  useEffect(() => {
    if (!open || !deviceCode) {
      setStageReady(false)
      return
    }
    loadState()
    const poll = setInterval(() => { if (!busyRef.current) loadState() }, 5000)
    const ready = setTimeout(() => setStageReady(true), 500)
    return () => { clearInterval(poll); clearTimeout(ready) }
  }, [open, deviceCode, loadState])

  // 환자 타임존 로드 시 기본 활성 슬롯을 환자 현지 현재 슬롯으로 보정(사용자 수동변경은 tz 불변이라 유지).
  useEffect(() => {
    if (device?.timezone) setActiveSlot(currentSlot(device.timezone))
  }, [device?.timezone])

  // 복용 5분 전 안내 + 하루치 복용 완료 칭찬 (20초마다 점검, 환자 TZ 기준)
  useEffect(() => {
    if (!open) return
    const id = setInterval(() => {
      const dev = deviceRef.current
      const pk = pocketsRef.current
      if (!dev || isVoiceMuted()) return
      const tc = todayColumn(dev.timezone)
      const nowMin = tzMinutes(dev.timezone)
      for (let s = 0; s < 4; s++) {
        const p = pk[tc * 4 + s]
        if (!p || !p.active || !p.filled) continue
        const st = dev.slot_times?.[SLOT_KEY[s]]
        if (!st) continue
        const [h, m] = st.split(':').map(Number)
        if (!Number.isFinite(h) || !Number.isFinite(m)) continue
        const slotMin = h * 60 + m
        const dkey = `${tc}_${s}`
        if (nowMin >= slotMin - 5 && nowMin < slotMin && !dueSaid.current.has(dkey)) {
          dueSaid.current.add(dkey)
          announceRef.current('due', tc, s)
        }
      }
      const todayActive = [0, 1, 2, 3].map((s) => pk[tc * 4 + s]).filter((p) => p?.active)
      if (todayActive.length > 0 && todayActive.every((p) => !p!.filled) && doneSaid.current !== String(tc)) {
        doneSaid.current = String(tc)
        announceRef.current('done')
      }
    }, 20000)
    return () => clearInterval(id)
  }, [open])

  const onPocketClick = async (idx: number) => {
    if (!deviceCode) return
    // 전주/다음주는 조회 전용 약달력 — 토글 시 '지금' intake 가 기록돼 데이터 오염되므로 차단.
    if (!(week?.is_current ?? true)) {
      pushLog(`· 전주/다음주는 조회 전용입니다 — 이번주에서만 복용 적재 가능`)
      return
    }
    const cur = pockets[idx]
    if (!cur || !cur.active) return
    const newFilled = !cur.filled
    busyRef.current = true
    setPockets((prev) => ({ ...prev, [idx]: { ...prev[idx], filled: newFilled } }))
    const action = newFilled ? 'close' : 'open'
    // 음성 안내: 채움(close) / 꺼냄(open) — 꺼냄은 현재 복용 시점·오늘이면 복용, 아니면 시기 아님
    const vSlot = idx % 4
    const vDow = Math.floor(idx / 4)
    if (newFilled) announce('fill', vDow, vSlot)
    else if (vSlot === currentSlot(device?.timezone) && vDow === todayCol) announce('take', vDow, vSlot)
    else announce('notyet')
    pushLog(`→ pharm/sim/${deviceCode}/pocket/${idx}/toggle  action=${action}`)
    try {
      const res = (await medApi.sim.toggle(deviceCode, idx, { action, source: 'web' })) as {
        fast_path?: {
          intake_log_inserted?: boolean
          schedule_id?: number | null
          status?: string | null
          delay_seconds?: number | null
        }
      }
      const fp = res?.fast_path
      // 정식 판정 결과(taken/late + 일정 연계)를 로그로 노출 — 환자별 프로세스 검증용
      let fpLog = ''
      if (fp) {
        if (fp.intake_log_inserted) {
          const sched = fp.schedule_id != null
            ? ` sched#${fp.schedule_id}` : ` (${t('LBL_NO_SCHEDULE_MATCH', '일정 미매칭 — schedule_id NULL')})`
          fpLog = ` [DB: pocket_state ✓ / intake_log ✓ status=${fp.status} delay=${fp.delay_seconds}s${sched}]`
        } else {
          fpLog = ' [DB: pocket_state ✓]'
        }
      }
      pushLog(`← pharm/device/${deviceCode}/pocket/${idx}/${newFilled ? 'closed' : 'opened'}  (source=sim)${fpLog}`)
      await loadState()
    } catch (e) {
      message.error(t('MSG_SIM_TOGGLE_FAIL', '시뮬 토글 실패') + ': ' + toErrMsg(e))
      await loadState()
    } finally {
      busyRef.current = false
    }
  }

  const onRefillAll = async () => {
    if (!deviceCode) return
    busyRef.current = true
    pushLog(`→ pharm/sim/${deviceCode}/refill (처방 일정 기준)`)
    try {
      const res = (await medApi.sim.refill(deviceCode, { operator: 'web' })) as { count?: number }
      await loadState()
      pushLog(`✓ refilled ${res?.count ?? ''} pockets — tb_pocket_state ✓ / tb_refill_log ✓`)
      message.success(t('MSG_REFILLED', '처방대로 리필 완료'))
      announce('refilled')
    } catch (e) {
      message.error(t('MSG_REFILL_FAIL', '리필 실패') + ': ' + toErrMsg(e))
    } finally {
      busyRef.current = false
    }
  }

  const onReset = async () => {
    if (!deviceCode) return
    busyRef.current = true
    pushLog(`→ pharm/sim/${deviceCode}/reset`)
    try {
      await medApi.sim.reset(deviceCode)
      await loadState()
      pushLog(`✓ 28 pockets is_filled=0 — Wokwi OLED "SIM RESET"`)
    } catch (e) {
      message.error(t('MSG_RESET_FAIL', '리셋 실패') + ': ' + toErrMsg(e))
    } finally {
      busyRef.current = false
    }
  }

  const onSlotChange = (val: number) => {
    setActiveSlot(val)
    if (deviceCode) pushLog(`→ pharm/cmd/${deviceCode}/led  slot=${SLOT_KEY[val]} action=blink  (복용 알림 점등)`)
  }

  // 미복용 감지 즉시 실행 — missed INSERT + 보호자별 alert fan-out 을 기다림 없이 검증
  const onRunMissed = async () => {
    busyRef.current = true
    pushLog('→ POST /api/v1/sim/cron/missed  (detect_missed_intakes 즉시 실행)')
    try {
      const res = await medApi.sim.cronMissed()
      pushLog(`✓ missed detect — inserted=${res?.inserted ?? 0} (intake_log missed + alert fan-out)`)
      message.success(`${t('MSG_MISSED_DONE', '미복용 감지 완료')} — ${res?.inserted ?? 0}`)
      await loadState()
    } catch (e) {
      message.error(t('MSG_MISSED_FAIL', '미복용 감지 실패') + ': ' + toErrMsg(e))
    } finally {
      busyRef.current = false
    }
  }

  const slotTimesText = SLOT_NAME.map((n, i) => `${n} ${device?.slot_times?.[SLOT_KEY[i]] ?? '--:--'}`).join('  ·  ')

  const hoverText = (() => {
    if (hoveredIdx == null) return null
    const day = DAY_LABEL[Math.floor(hoveredIdx / 4)]
    const slot = SLOT_NAME[hoveredIdx % 4]
    const p = pockets[hoveredIdx]
    const dow = `${day}${t('SIM_DAY_SUFFIX', '요일')}`
    if (!p || !p.active) return `${dow} ${slot} · ${t('SIM_HOVER_NO_RX', '처방 없음 (빈 슬롯)')}`
    const dosed = `${p.dose ?? 1}${t('SIM_UNIT_PILL', '정')}`
    const st = p.status ?? (p.filled ? 'pending' : 'taken')   // 대상 주 복용상태
    const state =
      st === 'missed' ? `${t('SIM_STATUS_MISSED', '미복용')} ✕`
      : st === 'late' ? t('SIM_STATUS_LATE', '지연 복용')
      : st === 'taken' ? `${t('SIM_HOVER_TAKEN', '복용 완료')} ✓`
      : t('SIM_HOVER_PENDING', '복용 대기')
    return `${dow} ${slot} · ${p.medName ?? t('SIM_MED', '약')} ${dosed} · ${state}`
  })()

  const list = Object.values(pockets)
  const activeCnt = list.filter((p) => p.active).length
  const filledCnt = list.filter((p) => p.active && p.filled).length

  return (
    <Modal
      title={`💊 ${deviceCode}`}
      open={open}
      onCancel={onClose}
      afterOpenChange={(opened) => { if (opened) setStageReady(true) }}
      footer={null}
      width="80vw"
      style={{ top: 20 }}
      destroyOnClose
    >
      {/* 환자 기준정보 헤더 — 이 디바이스가 누구의 약인지 명시 */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10, padding: '8px 12px', background: '#f0f5ff', borderRadius: 10, fontSize: 13 }}>
        <Tag color="blue" style={{ margin: 0 }}>{t('LBL_PATIENT', '환자')}</Tag>
        <strong style={{ fontSize: 14 }}>{device?.patient_name ?? '—'}</strong>
        <span style={{ color: '#8a93a0' }}>{device?.patient_code}</span>
        {device?.location && <span style={{ color: '#8a93a0' }}>· {device.location}</span>}
        <span style={{ color: '#8a93a0' }}>· {t('LBL_TODAY', '오늘')} {DAY_LABEL[todayCol]}{t('SIM_DAY_SUFFIX', '요일')}</span>
        <Tag color={device?.status === 'ACTIVE' ? 'green' : 'orange'} style={{ margin: 0 }}>{device?.status ?? '—'}</Tag>
        <span style={{ marginLeft: 'auto', color: '#8a93a0' }}>{t('LBL_FILLED', '채움')} {filledCnt}/{activeCnt}</span>
        <Tag
          color={voiceOn ? 'green' : 'default'}
          style={{ margin: 0, cursor: 'pointer' }}
          onClick={() => { const next = !voiceOn; setVoiceOn(next); setVoiceMuted(!next) }}
          title={t('SIM_VOICE_TOGGLE', '음성 안내')}
        >
          {voiceOn ? `🔊 ${t('SIM_VOICE_ON', '음성 켬')}` : `🔇 ${t('SIM_VOICE_OFF', '음성 끔')}`}
        </Tag>
      </div>
      {/* 주간 네비 — 현재 주간 복용일정 기준, 전주/이번주/다음주 이동 */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}>
        <Button size="small" onClick={() => setWeekOffset((w) => w - 1)}>← {t('SIM_PREV_WEEK', '전주')}</Button>
        <span style={{ fontWeight: 600, minWidth: 190, textAlign: 'center' }}>
          {week ? `${week.start} ~ ${week.end}` : ''}
          {week?.is_current ? ` (${t('SIM_THIS_WEEK', '이번주')})` : ''}
        </span>
        <Button size="small" type={weekOffset === 0 ? 'default' : 'primary'} disabled={weekOffset === 0}
          onClick={() => setWeekOffset(0)}>{t('SIM_THIS_WEEK', '이번주')}</Button>
        <Button size="small" onClick={() => setWeekOffset((w) => w + 1)}>{t('SIM_NEXT_WEEK', '다음주')} →</Button>
      </div>
      <Row gutter={16}>
        <Col xs={24} md={14}>
          <Card style={{ borderRadius: 14, overflow: 'hidden' }} bodyStyle={{ padding: 0, height: 500, display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 1, minHeight: 0, position: 'relative', background: 'linear-gradient(180deg, #e8eef5 0%, #c8d3df 100%)' }}>
              {stageReady ? (
                <Canvas shadows camera={{ position: [5, 7, 11], fov: 38 }} style={{ width: '100%', height: '100%' }} dpr={[1, 2]}>
                  <DispenserScene
                    pockets={pockets}
                    activeSlot={activeSlot}
                    todayCol={todayCol}
                    slotLabels={SLOT_NAME}
                    isCurrentWeek={week?.is_current ?? true}
                    onPocketClick={onPocketClick}
                    onHover={setHoveredIdx}
                  />
                </Canvas>
              ) : (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Spin />
                </div>
              )}
            </div>
            <Typography.Paragraph type="secondary" style={{ padding: 8, margin: 0, textAlign: 'center', flex: '0 0 auto', fontSize: 12 }}>
              {hoverText ?? ((week?.is_current ?? true)
                ? t('LBL_3D_HINT', '셀 LED 가 점멸하는 포켓이 지금 복용할 약 · 포켓 클릭 시 DB 즉시 적재 · 드래그로 회전')
                : t('SIM_HINT_READONLY', '전주/다음주 복용일정 (조회 전용) · 초록=복용 빨강=미복용 주황=지연 · 드래그로 회전'))}
            </Typography.Paragraph>
          </Card>
        </Col>
        <Col xs={24} md={10}>
          <SimControlPanel
            slotNames={SLOT_NAME}
            activeSlot={activeSlot}
            slotTimesText={slotTimesText}
            logs={logs}
            onSlotChange={onSlotChange}
            onRefill={onRefillAll}
            onReset={onReset}
            onRunMissed={onRunMissed}
          />
        </Col>
      </Row>
    </Modal>
  )
}
